import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const CHARGING_BASES = [
  { value: "PER_NIGHT_PER_ROOM", label: "Per night, per room", rule: "rate × nights × rooms" },
  { value: "PER_NIGHT_WHOLE_PROPERTY", label: "Per night, whole property", rule: "rate × nights (no room multiplier)" },
  { value: "PER_WEEK", label: "Per week", rule: "no total until the partial-week rule is recorded" },
  { value: "PER_STAY", label: "Per stay (fixed)", rule: "fixed total for the stated scope and dates" },
  { value: "OTHER", label: "Other (describe)", rule: "no automatic total" },
];
export const PER_WEEK_SCOPES = [{ value: "ROOM", label: "Per room" }, { value: "WHOLE_PROPERTY", label: "Whole property" }];
export const TAX_TREATMENTS = [{ value: "VAT_INCLUSIVE", label: "VAT inclusive" }, { value: "VAT_EXCLUSIVE", label: "Excluding VAT" }, { value: "VAT_EXEMPT", label: "VAT exempt" }, { value: "ZERO_RATED", label: "Zero-rated" }];
const NOT_RECORDED = <span className="text-jigsaw-muted">Not recorded</span>;

export const EMPTY_TERMS = { rate_amount: "", currency: "GBP", charging_basis: "", per_week_scope: "", charging_basis_detail: "", tax_treatment: "", inclusions_text: "" };

export function termsPayload(f) {
  return { rate_amount: Number(f.rate_amount), currency: f.currency, charging_basis: f.charging_basis || null, per_week_scope: f.charging_basis === "PER_WEEK" ? f.per_week_scope || null : null,
    charging_basis_detail: f.charging_basis_detail?.trim() || null, tax_treatment: f.tax_treatment || null,
    inclusions: f.inclusions_text?.trim() ? f.inclusions_text.split(",").map((s) => s.trim()).filter(Boolean) : null };
}
export function termsComplete(f) {
  if (!f.rate_amount || !f.currency || !f.charging_basis) return false;
  if (f.charging_basis === "PER_WEEK" && !f.per_week_scope) return false;
  if (f.charging_basis === "OTHER" && !f.charging_basis_detail?.trim()) return false;
  return true;
}
export function termsFromOffer(t) {
  return { rate_amount: t?.rate_amount ?? "", currency: t?.currency || "GBP", charging_basis: t?.charging_basis || "", per_week_scope: t?.per_week_scope || "", charging_basis_detail: t?.charging_basis_detail || "",
    tax_treatment: t?.tax_treatment || "", inclusions_text: (t?.inclusions || []).join(", ") };
}

/* Form fragment shared by the staff Record/Edit offer dialogs and the supplier quote form. Required: rate, currency, charging basis. */
export function TermsFields({ form, setForm, idPrefix = "offer" }) {
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const basis = CHARGING_BASES.find((b) => b.value === form.charging_basis);
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
        <div><Label>Rate <span className="text-jigsaw-danger">*</span></Label><Input data-testid={`${idPrefix}-rate-amount-input`} type="number" step="0.01" min="0" value={form.rate_amount} onChange={(e) => set("rate_amount")(e.target.value)} /></div>
        <div><Label>Currency <span className="text-jigsaw-danger">*</span></Label><Input data-testid={`${idPrefix}-currency-input`} maxLength={3} value={form.currency} onChange={(e) => set("currency")(e.target.value.toUpperCase())} /></div>
      </div>
      <div>
        <Label>Charging basis <span className="text-jigsaw-danger">*</span></Label>
        <Select value={form.charging_basis || undefined} onValueChange={(v) => setForm({ ...form, charging_basis: v, per_week_scope: v === "PER_WEEK" ? form.per_week_scope : "", charging_basis_detail: v === "OTHER" || v === "PER_STAY" ? form.charging_basis_detail : "" })}>
          <SelectTrigger data-testid={`${idPrefix}-charging-basis-select`}><SelectValue placeholder="Choose how the rate is charged" /></SelectTrigger>
          <SelectContent>{CHARGING_BASES.map((b) => <SelectItem key={b.value} value={b.value} data-testid={`${idPrefix}-charging-basis-${b.value}`}>{b.label}</SelectItem>)}</SelectContent>
        </Select>
        {basis && <p className="mt-1 text-[11px] text-jigsaw-muted">Calculation: {basis.rule}.</p>}
      </div>
      {form.charging_basis === "PER_WEEK" && (
        <div>
          <Label>Weekly rate covers <span className="text-jigsaw-danger">*</span></Label>
          <Select value={form.per_week_scope || undefined} onValueChange={set("per_week_scope")}>
            <SelectTrigger data-testid={`${idPrefix}-per-week-scope-select`}><SelectValue placeholder="Room or whole property" /></SelectTrigger>
            <SelectContent>{PER_WEEK_SCOPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {(form.charging_basis === "OTHER" || form.charging_basis === "PER_STAY") && (
        <div><Label>{form.charging_basis === "OTHER" ? <>Describe the basis <span className="text-jigsaw-danger">*</span></> : "Scope covered (optional)"}</Label><Input data-testid={`${idPrefix}-basis-detail-input`} value={form.charging_basis_detail} onChange={(e) => set("charging_basis_detail")(e.target.value)} placeholder={form.charging_basis === "OTHER" ? "e.g. per person per night, minimum 4 nights" : "e.g. 2 rooms, 14 nights"} /></div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label>Tax treatment <span className="text-jigsaw-muted">(optional)</span></Label>
          <Select value={form.tax_treatment || "NOT_RECORDED"} onValueChange={(v) => set("tax_treatment")(v === "NOT_RECORDED" ? "" : v)}>
            <SelectTrigger data-testid={`${idPrefix}-tax-treatment-select`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="NOT_RECORDED">Not recorded</SelectItem>{TAX_TREATMENTS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Inclusions <span className="text-jigsaw-muted">(optional, comma-separated)</span></Label><Input data-testid={`${idPrefix}-inclusions-input`} value={form.inclusions_text} onChange={(e) => set("inclusions_text")(e.target.value)} placeholder="e.g. Breakfast, Wi-Fi, Parking" /></div>
      </div>
    </>
  );
}

export function IndicativeTotal({ total, testId }) {
  if (!total) return null;
  return (
    <div data-testid={testId} className="rounded-md border border-jigsaw-border bg-jigsaw-bg px-3 py-2 text-[12px]">
      {total.amount != null ? (
        <>
          <p className="text-jigsaw-navy"><span className="font-semibold">Indicative total {total.amount} {total.currency}</span>{total.tax?.final_tax_inclusive ? <span className="ml-1 text-jigsaw-success">· VAT inclusive</span> : <span className="ml-1 text-jigsaw-warning">· not a final tax-inclusive total</span>}</p>
          <p className="text-jigsaw-muted">Basis: {total.basis_text}</p>
          {total.tax?.note && <p data-testid={testId ? `${testId}-tax-note` : undefined} className="text-jigsaw-muted">{total.tax.note}</p>}
        </>
      ) : (
        <>
          <p className="text-jigsaw-muted">No indicative total: {total.reason}</p>
          {total.basis_text && <p className="text-jigsaw-muted">Basis: {total.basis_text}</p>}
        </>
      )}
    </div>
  );
}

/* Read-only summary of an offer's terms (client-safe: no supplier notes). */
export function TermsSummary({ terms, testId, compact = false }) {
  if (!terms) return null;
  return (
    <div data-testid={testId} className="space-y-2 text-[13px]">
      <dl className={compact ? "grid grid-cols-2 gap-x-3 gap-y-1" : "grid gap-x-4 gap-y-1 sm:grid-cols-2"}>
        <div><dt className="text-[11px] uppercase tracking-wide text-jigsaw-muted">Rate</dt><dd className="font-mono-ref text-jigsaw-navy">{terms.rate_amount != null ? `${terms.rate_amount} ${terms.currency || ""}` : NOT_RECORDED}</dd></div>
        <div><dt className="text-[11px] uppercase tracking-wide text-jigsaw-muted">Charging basis</dt><dd data-testid={testId ? `${testId}-basis` : undefined} className="text-jigsaw-navy">{terms.charging_basis_label || NOT_RECORDED}</dd></div>
        <div><dt className="text-[11px] uppercase tracking-wide text-jigsaw-muted">Tax treatment</dt><dd data-testid={testId ? `${testId}-tax` : undefined} className="text-jigsaw-navy">{terms.tax_treatment_label || NOT_RECORDED}</dd></div>
        <div><dt className="text-[11px] uppercase tracking-wide text-jigsaw-muted">Inclusions</dt><dd data-testid={testId ? `${testId}-inclusions` : undefined} className="text-jigsaw-navy">{terms.inclusions == null ? NOT_RECORDED : terms.inclusions.length ? terms.inclusions.join(", ") : "None listed"}</dd></div>
      </dl>
      <IndicativeTotal total={terms.indicative_total} testId={testId ? `${testId}-total` : undefined} />
    </div>
  );
}

export function VersionChip({ terms, testId }) {
  if (!terms) return null;
  if (terms.is_superseded) return <span data-testid={testId} className="rounded-full border border-jigsaw-border bg-jigsaw-bg px-2 py-0.5 text-[11px] font-semibold text-jigsaw-muted">v{terms.version} · superseded</span>;
  if (terms.version > 1) return <span data-testid={testId} className="rounded-full border border-jigsaw-navy/20 bg-white px-2 py-0.5 text-[11px] font-semibold text-jigsaw-navy">v{terms.version} · replaces v{terms.version - 1}</span>;
  return null;
}
